import { UseMutationResult } from '@tanstack/react-query';
import { ChangeEvent, Dispatch, FormEvent, SetStateAction, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ProductFile } from 'src/pages/UploadPage/Uploader';
import { error as logError } from 'src/services/log';
import { postKitsIngest, postPartsCategoriesIngest, postSBOMIngest } from 'src/services/productInventory';

const allowedFormats = ['.zip', '.csv'];

interface POSTAPIResponse {
  url: string;
  runID: string;
  uploadURL: string;
}

type FileType = 'CAD' | 'sBOM' | 'PartsCategories' | 'Kits';

interface JobParams {
  fileType: FileType;
  account: string;
  cadJobRunID?: string;
  configurationPID?: string;
}

interface UploadFormParams {
  setUploadedCadFile: Dispatch<SetStateAction<ProductFile[]>>;
  setIsIngestionComplete?: Dispatch<SetStateAction<boolean>>;
  setUploadRunID: Dispatch<SetStateAction<string>>;
  uploadFile: () => void;
  isUploadComplete: boolean;
  mutation: UseMutationResult<unknown, unknown, string, unknown>;
  jobParams: JobParams;
}

export function useUploadForm({
  setUploadedCadFile,
  setIsIngestionComplete,
  setUploadRunID,
  uploadFile,
  isUploadComplete,
  mutation,
  jobParams,
}: UploadFormParams) {
  const [productName, setProductName] = useState<string>('');
  const [cadFile, setCadFile] = useState<File | null>(null);
  const [cadFileName, setCadFileName] = useState<string>('');
  const [isTriggerError, setIsTriggerError] = useState<boolean>(false);
  const [isTriggerSuccess, setIsTriggerSuccess] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [runID, setRunID] = useState<string>('');
  const [isIngestInProgress, setIsIngestInProgress] = useState<boolean>(false);

  useEffect(() => {
    resetForm();
  }, []);

  // Wait for upload to succeed before triggering followups
  useEffect(() => {
    /* istanbul ignore next - this hook is now obsolete and will be removed soon */
    if (isUploadComplete && !isTriggerSuccess && !isIngestInProgress) {
      if (jobParams.fileType === 'sBOM') {
        setIsIngestInProgress(true);
        postSBOMIngest(jobParams.account, runID, {
          configurationPID: jobParams.configurationPID,
          cadJobRunID: jobParams.cadJobRunID,
        })
          .then(() => {
            setIsIngestInProgress(false);
            setIsTriggerSuccess(true);
          })
          .catch(
            /* istanbul ignore next */ (error) => {
              setIsIngestInProgress(false);
              setIsTriggerError(true);
              logError(error);
            }
          );
      }

      if (jobParams.fileType === 'PartsCategories') {
        setIsIngestInProgress(true);
        postPartsCategoriesIngest({ accountId: jobParams.account, runID })
          .then(() => {
            setIsIngestInProgress(false);
            setIsTriggerSuccess(true);
          })
          .catch(
            /* istanbul ignore next */ (error) => {
              setIsIngestInProgress(false);
              setIsTriggerError(true);
              logError(error);
            }
          );
      }

      if (jobParams.fileType === 'Kits') {
        setIsIngestInProgress(true);
        postKitsIngest({ accountId: jobParams.account, runID })
          .then(() => {
            setIsIngestInProgress(false);
            setIsTriggerSuccess(true);
          })
          .catch(
            /* istanbul ignore next */ (error) => {
              setIsIngestInProgress(false);
              setIsTriggerError(true);
              logError(error);
            }
          );
      }
    }
  }, [runID, isUploadComplete, isTriggerSuccess, jobParams, isIngestInProgress]);

  /* istanbul ignore next */
  const handleNameChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setProductName(e.target.value);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    /* istanbul ignore next */
    if (!file || !allowedFormats.some((format) => file?.name.toLowerCase().includes(format))) {
      // Throw a toast error, in another ticket
      return;
    }

    setCadFile(file);
    setCadFileName(file.name);
  };

  const resetForm = async () => {
    // Reset form
    setProductName('');
    setCadFile(null);
    setCadFileName('');
    setIsTriggerError(false);
    setIsTriggerSuccess(false);

    /* istanbul ignore next */
    // Reset file input element
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.files = null;
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setIsTriggerError(false);
    setIsTriggerSuccess(false);

    /* istanbul ignore next */
    if (cadFile == null) {
      return;
    }

    /* istanbul ignore next */
    if (setIsIngestionComplete) {
      setIsIngestionComplete(false);
    }

    // Throw a toast error, in another ticket
    const response = await mutation.mutateAsync(productName);

    const { uploadURL, runID } = response as POSTAPIResponse;

    /* istanbul ignore next */
    if (!uploadURL || !runID) return; // Throw a toast error, in another ticket

    flushSync(() => {
      setUploadedCadFile([
        {
          file: cadFile,
          name: cadFileName,
          uploadState: 'initial',
          presignedUrl: {
            url: uploadURL,
            name: cadFileName,
          },
          products: undefined,
          description: undefined,
          size: (cadFile as any).size,
        },
      ]);

      setRunID(runID);
      setUploadRunID(runID);
      uploadFile();
    });
  };

  return {
    productName,
    setProductName,
    cadFileName,
    setCadFileName,
    handleNameChange,
    resetForm,
    handleFileChange,
    handleSubmit,
    cadFile,
    setCadFile,
    fileInputRef,
    isTriggerError,
    isTriggerSuccess,
    isIngestInProgress,
    runID,
  };
}
